<script lang="ts">
  import { onMount } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import { calculatePercentage } from '../lib/utils/calculations';
  import type { Summary } from '../types/transaction';

  let { summary }: { summary: Summary | null } = $props();
  let canvas: HTMLCanvasElement;
  let chart: Chart | null = null;

  onMount(() => {
    Chart.register(...registerables);
    renderChart();

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  });

  // Re-render chart when summary changes
  $effect(() => {
    if (canvas && summary) {
      renderChart();
    }
  });

  function renderChart() {
    // Destroy existing chart before creating new one
    if (chart) {
      chart.destroy();
      chart = null;
    }

    // Guard: Check if summary has required data
    if (!summary || !summary.byCategory) {
      return;
    }

    // Get all expense categories sorted by amount
    // Note: Income, transfers, and CC payments are already excluded in analytics.ts
    const expenseCategories = Object.entries(summary.byCategory)
      .sort(([, a], [, b]) => b.amount - a.amount);

    // If no expense categories found, don't render chart
    if (expenseCategories.length === 0) {
      return;
    }

    // Take top 15 categories, group the rest as "All Others"
    const topCategories = expenseCategories.slice(0, 15);
    const remainingCategories = expenseCategories.slice(15);

    const mainCategories: [string, any][] = [...topCategories];

    // Add "All Others" if there are remaining items
    if (remainingCategories.length > 0) {
      const othersAmount = remainingCategories.reduce((sum, [, stats]) => sum + stats.amount, 0);
      mainCategories.push(['All Others', { amount: othersAmount, count: 0 }]);
    }

    const labels = mainCategories.map(([name]) => name);
    const data = mainCategories.map(([, stats]) => stats.amount);

    // Get colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    const baseColors = [
      rootStyles.getPropertyValue('--chart-color-1').trim(),
      rootStyles.getPropertyValue('--chart-color-4').trim(),
      rootStyles.getPropertyValue('--chart-color-2').trim(),
      rootStyles.getPropertyValue('--chart-color-9').trim(),
      rootStyles.getPropertyValue('--chart-color-7').trim(),
      rootStyles.getPropertyValue('--chart-color-6').trim(),
      rootStyles.getPropertyValue('--chart-color-3').trim(),
      rootStyles.getPropertyValue('--chart-color-10').trim(),
      rootStyles.getPropertyValue('--chart-color-8').trim(),
      rootStyles.getPropertyValue('--chart-color-5').trim(),
    ];

    // Cycle through colors if we have more categories than colors
    const colors = labels.map((_, i) => baseColors[i % baseColors.length]);

    const borderColor = rootStyles.getPropertyValue('--color-text-primary').trim();
    const legendColor = rootStyles.getPropertyValue('--color-text-muted').trim();

    chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors,
          borderColor: borderColor,
          borderWidth: 0,
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed.x as number;
                const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
                const percentage = calculatePercentage(value, total).toFixed(1);
                const formatted = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(value);
                return `${formatted} (${percentage}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              color: legendColor,
              callback: function(value) {
                return '$' + value.toLocaleString();
              }
            },
            grid: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-border').trim()
            }
          },
          y: {
            ticks: {
              color: legendColor,
              font: {
                size: 11
              }
            },
            grid: {
              display: false
            }
          }
        }
      }
    });
  }
</script>

<div class="chart-container">
  <canvas bind:this={canvas}></canvas>
</div>

<style lang="scss">
  .chart-container {
    position: relative;
    width: 100%;
    height: 350px;
  }
</style>
